import { useTranslation } from 'react-i18next';

import { modalState } from '@/state/modal.state';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';

import { AppUsersTable } from '../../app-users/app-users-table';
import { AppUserEmptyState } from '../../app-users/empty-state';
import { AppUsersTableNoResults } from '../../app-users/no-results';
import { UserSearch } from '../../app-users/user-search';
import {
  settingsInvitedUsersSearchPhraseState,
  settingsInvitedUsersSortState,
} from '../settings.state';

import { useInvitedUsers } from './use-invited-users';
import { useInvitedUsersTableConfiguration } from './use-invited-users-table-configuration';
import type { UsersListSort } from '@/api/v4/user.api';
import { useAtom, useSetAtom } from 'jotai';
import { Flexbox } from '@/ui/flexbox/flexbox';

export const InvitedUsers = () => {
  const { t } = useTranslation('settings');
  const setModal = useSetAtom(modalState);
  const { hasRequiredPermission } = useUserPermissions();
  const hasManageTeamPermission = hasRequiredPermission(
    PERMISSION.MANAGE_TEAMS,
  );
  const { invitedUsers, isLoading } = useInvitedUsers();

  const [userSearchPhrase, setUserSearchPhrase] = useAtom(
    settingsInvitedUsersSearchPhraseState,
  );
  const { header, rows } = useInvitedUsersTableConfiguration(invitedUsers);

  const resetInvitedUsersSearchPhrase = () => setUserSearchPhrase('');

  const handleInviteUser = () => setModal({ state: 'inviteUser' });

  return (
    <>
      <Flexbox justify="right">
        <UserSearch
          onSearchInputChange={e => {
            setUserSearchPhrase(e.target.value);
          }}
          translationNamespace="settings"
        />
      </Flexbox>
      <AppUsersTable<UsersListSort>
        users={invitedUsers}
        isLoading={isLoading}
        withSearchPhrase={Boolean(userSearchPhrase)}
        sortState={settingsInvitedUsersSortState}
        hasNextPage={false}
        noResultsElement={
          <AppUsersTableNoResults
            onReset={resetInvitedUsersSearchPhrase}
            resetResultsText={t`resetResults`}
            noResultsText={t`noResults`}
          />
        }
        emptyStateElement={
          <AppUserEmptyState
            onClick={handleInviteUser}
            buttonText={t`default:invite.inviteUser`}
            text={t`emptyUsers`}
            disabled={!hasManageTeamPermission}
          />
        }
        header={header}
        rows={rows}
      />
    </>
  );
};
